import type { CellTypeSignal } from "./types";

/**
 * Direct-detection methods for cell types. These look only at the cell
 * itself (value, value type, number format) — not at how the cell is used
 * by other formulas (that's `inference.ts`).
 */

// Same conventions as the existing `dateFormatCells` audit rule:
// strip text literals, escaped chars, and bracket clauses before scanning
// for tokens.
const DATE_TOKEN_RE = /[yd]|m{3,}/i;
const TIME_TOKEN_RE = /h|s|am\/pm/i;
const PERCENT_TOKEN_RE = /%/;
// Currency tokens are tested against the un-stripped format because bracketed
// locale clauses like [$USD] are themselves currency indicators.
const CURRENCY_TOKEN_RE = /[$£¥€]|\[\$/;

function stripFormat(fmt: string): string {
  return fmt
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*\]/g, "");
}

export function detectFromValueType(valueType: Excel.RangeValueType): CellTypeSignal | null {
  switch (valueType) {
    case Excel.RangeValueType.boolean:
      return { type: "boolean", source: "valueType:boolean", confidence: "high" };
    case Excel.RangeValueType.error:
      return { type: "error", source: "valueType:error", confidence: "high" };
    case Excel.RangeValueType.string:
      return { type: "text", source: "valueType:string", confidence: "high" };
    case Excel.RangeValueType.empty:
      return { type: "empty", source: "valueType:empty", confidence: "high" };
    case Excel.RangeValueType.double:
    case Excel.RangeValueType.integer:
      // Just numeric — could be plain number, date serial, currency, percent.
      // Number format and value-range detectors narrow this down.
      return { type: "number", source: "valueType:numeric", confidence: "medium" };
    default:
      return null;
  }
}

export function detectFromFormat(fmt: string | undefined | null): CellTypeSignal[] {
  if (!fmt || fmt === "General") return [];
  const stripped = stripFormat(fmt);
  const isDate = DATE_TOKEN_RE.test(stripped);
  const isTime = TIME_TOKEN_RE.test(stripped);

  if (isDate && isTime) {
    return [{ type: "datetime", source: `numberFormat:datetime(${fmt})`, confidence: "high" }];
  }
  if (isDate) {
    return [{ type: "date", source: `numberFormat:date(${fmt})`, confidence: "high" }];
  }
  if (isTime) {
    return [{ type: "time", source: `numberFormat:time(${fmt})`, confidence: "high" }];
  }
  if (PERCENT_TOKEN_RE.test(stripped)) {
    return [{ type: "percent", source: `numberFormat:percent(${fmt})`, confidence: "high" }];
  }
  if (CURRENCY_TOKEN_RE.test(fmt)) {
    return [{ type: "currency", source: `numberFormat:currency(${fmt})`, confidence: "high" }];
  }
  return [];
}

// Loose text patterns — only emit at low confidence. Lots of false positives
// possible (e.g., a part number "2024-01-15" is not a date), so these need
// the conflict-detection layer to flag them when usage disagrees.
const TEXT_DATE_RE =
  /^(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{1,2}-[A-Za-z]{3}-\d{2,4})$/;
const TEXT_NUMBER_RE = /^-?\d+(?:\.\d+)?$/;
const TEXT_PERCENT_RE = /^-?\d+(?:\.\d+)?\s*%$/;
const TEXT_CURRENCY_RE = /^[$£¥€]\s*-?\d/;

export function detectFromTextValue(value: string): CellTypeSignal[] {
  const t = value.trim();
  if (!t) return [];
  if (TEXT_DATE_RE.test(t)) {
    return [{ type: "date", source: "textPattern:dateLike", confidence: "low" }];
  }
  if (TEXT_PERCENT_RE.test(t)) {
    return [{ type: "percent", source: "textPattern:percentLike", confidence: "low" }];
  }
  if (TEXT_CURRENCY_RE.test(t)) {
    return [{ type: "currency", source: "textPattern:currencyLike", confidence: "low" }];
  }
  if (TEXT_NUMBER_RE.test(t)) {
    return [{ type: "number", source: "textPattern:numberLike", confidence: "low" }];
  }
  return [];
}

/**
 * Plausibility check: a whole-number value in the modern Excel date-serial
 * range (1970-01-01 .. 2100-12-31) could be a date stored without a date
 * format. Low confidence by itself; useful as corroboration when usage
 * inference also suggests "date".
 */
export function detectFromValueRange(value: number): CellTypeSignal[] {
  if (Number.isInteger(value) && value >= 25569 && value <= 73415) {
    return [{ type: "date", source: "valueRange:dateSerial", confidence: "low" }];
  }
  return [];
}
